import React, { useState, useEffect } from 'react';
import { Minus, Plus, QrCode, CreditCard, ArrowLeft, Lock, ShieldCheck } from 'lucide-react';
import { FaFileInvoiceDollar } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useHistory, useParams } from 'react-router-dom';
import { PLAN_OPTIONS, PlanType, calcularPrecoLocaSmartCheck } from '../../../services/tipos';
import logoLocaSmartCheck from '../../../assets/logo-locasmartcheck.png';

interface CheckoutFormProps {
  vehicleCount: number;
  setVehicleCount: (count: number) => void;
  paymentMethod: 'pix' | 'creditcard';
  selectedPlan: PlanType;
  setSelectedPlan: (plan: PlanType) => void;
  pixData?: {
    qrCode: string;
    qrCodeImage: string;
    paymentUrl: string;
    pixKey: string;
  } | null;
  isSubmitting: boolean;
  onCheckout: () => void;
  formErrors: {
    cardName?: string;
    cardNumber?: string;
    expiration?: string;
    cvv?: string;
    cpf?: string;
    postalCode?: string;
    addressNumber?: string;
    phone?: string;
  };
  hasActivePayment: boolean;
  hasPendingInvoice: boolean;
  minVehicleLimit: number;
  isExpansion: boolean;
  isUpgrade: boolean;
  incluirLocaSmartCheck: boolean;
  setIncluirLocaSmartCheck: (val: boolean) => void;
  isLocaSmartCheckAtivo: boolean;
  precoLocaSmartCheckMensal: number;
  planDiscount: number;
  secao?: 'config' | 'pagamento' | 'tudo';
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  vehicleCount,
  setVehicleCount,
  paymentMethod,
  selectedPlan,
  setSelectedPlan,
  pixData,
  isSubmitting,
  onCheckout,
  formErrors,
  hasActivePayment,
  hasPendingInvoice,
  minVehicleLimit,
  isExpansion,
  isUpgrade,
  incluirLocaSmartCheck,
  setIncluirLocaSmartCheck,
  isLocaSmartCheckAtivo,
  precoLocaSmartCheckMensal,
  planDiscount,
  secao = 'tudo'
}) => {
  const [isButtonsDisabled, setIsButtonsDisabled] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const history = useHistory();
  const { plano } = useParams<{ plano: string }>();

  useEffect(() => {
    if (plano && !isExpansion && !isUpgrade) {
      const planMapping: Record<string, PlanType> = {
        'mensal': 'MONTHLY',
        'trimestral': 'QUARTERLY',
        'semestral': 'SEMIANNUAL',
        'anual': 'ANNUAL'
      };
      
      const mappedPlan = planMapping[plano.toLowerCase()];
      if (mappedPlan) {
        setSelectedPlan(mappedPlan);
      }
    }
  }, [plano, setSelectedPlan, isExpansion, isUpgrade]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (pixData && isButtonsDisabled) {
      setCountdown(120);
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setIsButtonsDisabled(false);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(timer);
  }, [pixData, isButtonsDisabled]);

  const handleDecrease = () => {
    if (vehicleCount > minVehicleLimit) {
       setVehicleCount(vehicleCount - 5);
    }
  };

  const handleIncrease = () => {
    setVehicleCount(vehicleCount + 5);
  };

  const handleBigStep = (step: number) => {
    const newCount = vehicleCount + step;
    
    if (newCount < minVehicleLimit) {
       setVehicleCount(minVehicleLimit);
    } else {
       setVehicleCount(newCount);
    }
  };

  const formatCardNumber = (value: string) => {
    return value.replace(/\D/g, '')
      .replace(/(\d{4})(?=\d)/g, '$1 ')
      .substring(0, 19);
  };

  const formatExpiration = (value: string) => {
    return value.replace(/\D/g, '')
      .replace(/(\d{2})(?=\d)/g, '$1/')
      .substring(0, 5);
  };

  const formatCPF = (value: string) => {
    return value.replace(/\D/g, '')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})/, '$1-$2')
      .substring(0, 14);
  };

  const formatPostalCode = (value: string) => {
    return value.replace(/\D/g, '')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .substring(0, 9);
  };

  const formatPhone = (value: string) => {
    return value.replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .substring(0, 15);
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = formatCardNumber(e.target.value);
  };

  const handleExpirationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = formatExpiration(e.target.value);
  };

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = formatCPF(e.target.value);
  };

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = formatPostalCode(e.target.value);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.target.value = formatPhone(e.target.value);
  };

  const handleBackToPlans = () => {
    history.push('/planos');
  };

  const selectedPlanOption = PLAN_OPTIONS.find(plan => plan.id === selectedPlan);
  const isDecreaseDisabled = vehicleCount <= minVehicleLimit;

  const isProcessingCard = isSubmitting && paymentMethod === 'creditcard';

  return (
    <div className="space-y-4 text-md ">
      {!isProcessingCard && hasActivePayment && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                Você já tem um pagamento em processamento. Aguarde a conclusão antes de fazer novas alterações.
              </p>
            </div>
          </div>
        </div>
      )}

      {!isProcessingCard && secao !== 'pagamento' && <>
      {secao !== 'config' && (
      <div>
        <h2 className="text-base font-semibold text-[#1F467E] mb-1 flex items-center">
          {paymentMethod === 'pix' ? (
            <>
              <QrCode className="mr-2 text-[#1F467E]" size={18} />
              Pagamento via PIX
            </>
          ) : (
            <>
              <CreditCard className="mr-2 text-[#1F467E]" size={18} />
              Pagamento com Cartão de Crédito
            </>
          )}
        </h2>
        <p className="text-gray-600">
          {paymentMethod === 'pix'
            ? 'Gere o QR Code para pagamento rápido e seguro.'
            : 'Configure sua assinatura recorrente.'}
        </p>
      </div>
      )}

      <div className="p-4 border border-blue-100 rounded-lg bg-white shadow-sm">
        <label className="block text-[#1F467E] font-medium text-sm mb-3">
          Limite de Veículos:
        </label>

        <div className="flex items-center justify-between mb-3">
          <button
            onClick={handleDecrease}
            disabled={isDecreaseDisabled || isButtonsDisabled || isSubmitting || hasActivePayment || hasPendingInvoice}
            className={`flex items-center justify-center w-10 h-10 rounded-full border ${isDecreaseDisabled || isButtonsDisabled || isSubmitting || hasActivePayment
              ? 'border-gray-200 text-gray-400 cursor-not-allowed'
              : 'border-blue-300 text-[#1F467E] hover:bg-blue-50'
              }`}
          >
            <Minus size={16} />
          </button>

          <div className="flex flex-col items-center">
            <span className="text-3xl font-bold text-[#1F467E]">{vehicleCount}</span>
            <span className="text-xs text-gray-500">veículos</span>
          </div>

          <button
            onClick={handleIncrease}
            disabled={isButtonsDisabled || isSubmitting || hasActivePayment || hasPendingInvoice}
            className={`flex items-center justify-center w-10 h-10 rounded-full border ${isButtonsDisabled || isSubmitting || hasActivePayment || hasPendingInvoice
              ? 'border-gray-200 text-gray-400 cursor-not-allowed'
              : 'border-blue-300 text-[#1F467E] hover:bg-blue-50'
              }`}
          >
            <Plus size={16} />
          </button>
        </div>

        <div className="grid grid-cols-4 gap-2">
          {[5, 10, 15, 20].map((step) => {
            const willGoBelowLimit = (vehicleCount + step) < minVehicleLimit && step < 0; 
            return (
            <button
              key={step}
              onClick={() => handleBigStep(step)}
              disabled={isButtonsDisabled || isSubmitting || hasActivePayment || hasPendingInvoice || (step < 0 && isDecreaseDisabled)}
              className={`py-1.5 text-xs rounded-md ${isButtonsDisabled || isSubmitting || hasActivePayment || hasPendingInvoice || (step < 0 && isDecreaseDisabled)
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : step > 0
                  ? 'bg-blue-50 text-[#1F467E] hover:bg-blue-100'
                  : 'bg-red-50 text-red-600 hover:bg-red-100'
                }`}
            >
              {step > 0 ? `+${step}` : step}
            </button>
          )})}
        </div>
      </div>

      {!isExpansion && !isUpgrade && (
        <div className="p-4 border border-blue-100 rounded-lg bg-white shadow-sm">
          <label className="block text-[#1F467E] font-medium text-sm mb-3">
            Ciclo de Pagamento
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {PLAN_OPTIONS.map((option) => (
              <button
                key={option.id}
                onClick={() => setSelectedPlan(option.id)}
                disabled={isButtonsDisabled || isSubmitting || hasActivePayment || hasPendingInvoice}
                className={`relative flex flex-col items-center justify-center p-3 border rounded-lg transition-all ${
                  selectedPlan === option.id
                    ? 'border-[#1F467E] bg-blue-50 ring-1 ring-[#1F467E]'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                } ${isButtonsDisabled || isSubmitting || hasActivePayment || hasPendingInvoice ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                {option.discount > 0 && (
                  <span className="absolute -top-2 right-2 bg-green-100 text-green-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    -{option.discount * 100}%
                  </span>
                )}
                <span className={`text-sm font-medium ${selectedPlan === option.id ? 'text-[#1F467E]' : 'text-gray-600'}`}>
                  {option.name}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* LocaSmartCheck Addon — compacto */}
      {!isExpansion && !isUpgrade && !isLocaSmartCheckAtivo && (
        <div
          onClick={() => setIncluirLocaSmartCheck(!incluirLocaSmartCheck)}
          className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${
            incluirLocaSmartCheck
              ? 'border-[#1F467E] bg-blue-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
            incluirLocaSmartCheck ? 'bg-[#1F467E] border-[#1F467E]' : 'border-gray-300'
          }`}>
            {incluirLocaSmartCheck && (
              <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>

          <img src={logoLocaSmartCheck} alt="LocaSmartCheck" className="w-10 h-10 rounded-lg object-contain border border-gray-200 bg-white flex-shrink-0" />

          <div className="min-w-0 flex-1">
            <p className="font-bold text-[#0f172a] text-sm leading-tight mb-0">LocaSmartCheck</p>
            <p className="text-xs text-gray-500 leading-tight mb-0">App de vistorias: fotos, assinatura digital do locatário e laudo em PDF</p>
          </div>

          <span className="flex-shrink-0 whitespace-nowrap font-extrabold text-[#1F467E] text-sm bg-white border border-gray-200 rounded-lg px-2.5 py-1">
            {/* Preco com o MESMO desconto de ciclo do plano — coerente com o resumo e o total.
                Nao usa precoLocaSmartCheckMensal (que e 0 enquanto o addon nao esta marcado). */}
            +{(calcularPrecoLocaSmartCheck(vehicleCount) * (1 - planDiscount)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            <span className="text-[10px] font-medium text-gray-400">/mês</span>
          </span>
        </div>
      )}
      </>}

      {secao !== 'config' && paymentMethod === 'creditcard' && (
        isSubmitting ? (
          <div className="p-8 border border-blue-100 rounded-lg bg-white shadow-sm">
            <div className="flex flex-col items-center justify-center py-6">
              {/* Animated card icon */}
              <div className="relative mb-6">
                <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center">
                  <CreditCard size={36} className="text-[#1F467E] animate-pulse" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-green-50 flex items-center justify-center border-2 border-white">
                  <ShieldCheck size={16} className="text-green-500" />
                </div>
              </div>

              {/* Spinner */}
              <div className="mb-5">
                <svg className="animate-spin h-8 w-8 text-[#1F467E]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>

              <h3 className="text-lg font-bold text-[#1F467E] mb-2">Processando Pagamento</h3>
              <p className="text-sm text-gray-500 text-center max-w-xs mb-4">
                Estamos validando os dados do seu cartao junto a operadora. Isso pode levar alguns segundos.
              </p>

              {/* Progress steps */}
              <div className="w-full max-w-xs space-y-2.5">
                <div className="flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <span className="text-sm text-gray-600">Dados do cartao enviados</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-[#1F467E] flex items-center justify-center flex-shrink-0 animate-pulse">
                    <div className="w-2 h-2 rounded-full bg-white" />
                  </div>
                  <span className="text-sm text-[#1F467E] font-medium">Aguardando confirmacao da operadora...</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-gray-200 flex-shrink-0" />
                  <span className="text-sm text-gray-400">Ativando seu plano</span>
                </div>
              </div>

              <p className="text-xs text-gray-400 mt-6">
                Nao feche esta pagina durante o processamento.
              </p>
            </div>
          </div>
        ) : (
          <div className="relative p-4 border border-blue-100 rounded-lg bg-white space-y-3 shadow-sm">
            {hasActivePayment && (
              <div className="absolute inset-0 bg-white bg-opacity-70 z-10 flex items-center justify-center rounded-lg">
                <p className="text-sm text-[#1F467E] font-medium">Pagamento em processamento</p>
              </div>
            )}
            <div>
              <label htmlFor="card-name" className="block mb-1 text-[#1F467E] text-sm font-medium">
                Nome no Cartao
              </label>
              <input
                id="card-name"
                type="text"
                placeholder="Nome como no cartao"
                className={`w-full h-10 px-3 py-2 border rounded-md text-sm ${formErrors.cardName ? 'border-red-500' : 'border-blue-200'
                  }`}
                required
              />
              {formErrors.cardName && (
                <p className="text-red-500 text-xs mt-1">{formErrors.cardName}</p>
              )}
            </div>

            <div>
              <label htmlFor="card-number" className="block mb-1 text-[#1F467E] text-sm font-medium">
                Numero do Cartao
              </label>
              <input
                id="card-number"
                type="text"
                placeholder="0000 0000 0000 0000"
                maxLength={19}
                className={`w-full h-10 px-3 py-2 border rounded-md text-sm ${formErrors.cardNumber ? 'border-red-500' : 'border-blue-200'
                  }`}
                onChange={handleCardNumberChange}
                required
              />
              {formErrors.cardNumber && (
                <p className="text-red-500 text-xs mt-1">{formErrors.cardNumber}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="expiration" className="block mb-1 text-[#1F467E] text-sm font-medium">
                  Validade (MM/AA)
                </label>
                <input
                  id="expiration"
                  type="text"
                  placeholder="MM/AA"
                  maxLength={5}
                  className={`w-full h-10 px-3 py-2 border rounded-md text-sm ${formErrors.expiration ? 'border-red-500' : 'border-blue-200'
                    }`}
                  onChange={handleExpirationChange}
                  required
                />
                {formErrors.expiration && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.expiration}</p>
                )}
              </div>

              <div>
                <label htmlFor="cvv" className="block mb-1 text-[#1F467E] text-sm font-medium">
                  CVV
                </label>
                <input
                  id="cvv"
                  type="text"
                  placeholder="000"
                  maxLength={4}
                  className={`w-full h-10 px-3 py-2 border rounded-md text-sm ${formErrors.cvv ? 'border-red-500' : 'border-blue-200'
                    }`}
                  required
                />
                {formErrors.cvv && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.cvv}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="cpf" className="block mb-1 text-[#1F467E] text-sm font-medium">
                CPF do Titular
              </label>
              <input
                id="cpf"
                type="text"
                placeholder="000.000.000-00"
                maxLength={14}
                className={`w-full h-10 px-3 py-2 border rounded-md text-sm ${formErrors.cpf ? 'border-red-500' : 'border-blue-200'
                  }`}
                onChange={handleCPFChange}
                required
              />
              {formErrors.cpf && (
                <p className="text-red-500 text-xs mt-1">{formErrors.cpf}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="postal-code" className="block mb-1 text-[#1F467E] text-sm font-medium">
                  CEP
                </label>
                <input
                  id="postal-code"
                  type="text"
                  placeholder="00000-000"
                  maxLength={9}
                  className={`w-full h-10 px-3 py-2 border rounded-md text-sm ${formErrors.postalCode ? 'border-red-500' : 'border-blue-200'
                    }`}
                  onChange={handlePostalCodeChange}
                  required
                />
                {formErrors.postalCode && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.postalCode}</p>
                )}
              </div>

              <div>
                <label htmlFor="address-number" className="block mb-1 text-[#1F467E] text-sm font-medium">
                  Numero
                </label>
                <input
                  id="address-number"
                  type="text"
                  placeholder="Numero"
                  className={`w-full h-10 px-3 py-2 border rounded-md text-sm ${formErrors.addressNumber ? 'border-red-500' : 'border-blue-200'
                    }`}
                  required
                />
                {formErrors.addressNumber && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.addressNumber}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block mb-1 text-[#1F467E] text-sm font-medium">
                Telefone
              </label>
              <input
                id="phone"
                type="text"
                placeholder="(00) 00000-0000"
                maxLength={15}
                className={`w-full h-10 px-3 py-2 border rounded-md text-sm ${formErrors.phone ? 'border-red-500' : 'border-blue-200'
                  }`}
                onChange={handlePhoneChange}
                required
              />
              {formErrors.phone && (
                <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>
              )}
            </div>
          </div>
        )
      )}

      {secao !== 'config' && paymentMethod === 'pix' && (
        <div className="p-6 flex flex-col items-center border border-blue-100 rounded-lg bg-white shadow-sm">
          {pixData ? (
            <>
              {pixData.qrCodeImage ? (
                <div className="w-40 h-40 bg-blue-50 flex items-center justify-center mb-4 rounded-lg border-2 border-dashed border-blue-300 shadow-sm">
                  <img
                    src={`data:image/png;base64,${pixData.qrCodeImage}`}
                    alt="QR Code PIX"
                    className="max-w-full max-h-full object-contain rounded-md"
                  />
                </div>
              ) : <></>}

              <div className="w-full space-y-4">
                {pixData.pixKey ? <div>
                  <label className="block text-[#1F467E] font-medium text-sm mb-2">
                    Chave PIX Copia e Cola
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      readOnly
                      value={pixData.pixKey}
                      className="w-full p-3 pr-10 border border-blue-200 rounded-md bg-blue-50 text-[#1F467E] text-xs font-mono"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(pixData.pixKey);
                        toast.success('Código PIX copiado!');
                      }}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-[#1F467E] hover:text-blue-800"
                      title="Copiar"
                      disabled={isSubmitting}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                  </div>
                </div> : <></>}

                {pixData.paymentUrl && (
                  <a
                    href={pixData.paymentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 w-fit mx-auto py-2 px-4 bg-[#1F467E] hover:bg-blue-800 text-white text-sm rounded-md transition-colors no-underline"
                  >
                    <FaFileInvoiceDollar className="text-white" />
                    Visualizar e Pagar Fatura
                  </a>
                )}

                <p className="text-sm text-center text-[#1F467E]">
                  {!pixData.pixKey && pixData.paymentUrl ? "Clique na Fatura acima para pagar a sua Pendência" : "Escaneie o QR Code ou copie o código para pagar via PIX."}
                </p>

                {isButtonsDisabled && (
                  <p className="text-sm text-center text-[#1F467E]">
                    Nova geração em: {countdown} segundos
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="w-40 h-40 bg-blue-50 flex items-center justify-center mb-4 rounded-lg border-2 border-dashed border-blue-200">
                <QrCode size={90} className="text-blue-400 opacity-70" />
              </div>
              <p className="text-sm text-center text-[#1F467E] max-w-xs mt-2">
                Clique no botão "Gerar QR Code PIX" no resumo ao lado.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CheckoutForm;